// Configuration for builds made by scripts/frontend/prepare-runtime-build.sh: the GitHub Actions
// (Pulumi) deploys and the local Docker environments. That script copies this file over
// src/config.js before `npm run build`. The e4enable servers' builds never run it, so they keep
// config.js and its hostname tables.
//
// Where the values come from, in order:
//   1. the defaults below: what a *.qa.richardsonaccelerate.com site got from config.js, with the
//      API on the site's own origin and no compiled-in API key;
//   2. window.__INSIGHTS_CONFIG__, set by /runtime-config.js. The prep script adds
//      <script src="/runtime-config.js"></script> to index.html ahead of the app bundle, so it has
//      already run when this module is evaluated. The deploy writes that file from Pulumi; a local
//      container's nginx writes it from E4_* variables.
//
// Every key, who writes it and its default: docs/frontend-runtime-config.md.
// The shared constants are copied from config.js; config.runtime.test.js fails if they drift.

const origin = window.location.origin.toLowerCase();

// Copied from config.js's export (everything before `...config`).
const shared = {
    MAX_ATTACHMENT_SIZE: 10485760, // 10 MB
    MAX_VIDEO_SIZE: 524288000, // 500 MB
    mediaExtensions: ['.m4a', '.m4v', '.mov', '.mp3', '.mp4', '.ogg', '.webm', '.wmv'],
    // Off on every deployed environment, unlike config.js's QA hosts: axe only logs to the
    // browser console, slows every page, and its re-runs reached Honeybadger as
    // "Axe is already running". Run it locally (config.js) when you want it.
    useAxe: false,
    storage: {
        URL: 'https://insightsmedia.richardsonaccelerate.com/',
        ScormWrapper: 'scorm/scorm-wrapper-999.html',
    },
    hubspot: {
        auth: 'https://app.hubspot.com/oauth/authorize?client_id=b21b5f1e-5bb8-4198-9a21-a59de1996517&redirect_uri=https://insights.richardsonaccelerate.com/oauth/hubspot&scope=crm.lists.read%20crm.objects.contacts.read%20crm.schemas.contacts.read%20crm.objects.companies.read%20crm.objects.deals.read%20crm.schemas.companies.read%20crm.schemas.deals.read%20settings.users.read',
    },
    outreach: {
        applicationId: '15HLnKe3ZYVY3cz35VPUuO09FT__8BqpBrqb-urhewg',
        scopes: 'calls.read+mailings.read+users.read+callDispositions.read+tasks.read',
        auth: 'https://api.outreach.io/oauth/authorize?client_id=<Application_Identifier>&redirect_uri=<Redirect_Uri>&response_type=code&scope=<Scopes>',
    },
    salesforce: {
        clientId: '3MVG9L8AofS.N0y68nzO2PJwzCY8kLjSfFKQSLmxEbak70IkVWfWom7GQZtTMIw9_72rtJIXrLGdHUFjmwBOU',
    },
    showpad: {
        auth: 'https://<Subdomain>.showpad.biz/api/v3/oauth2/authorize?client_id=<Client_Id>&redirect_uri=<Redirect_Uri>&response_type=code',
    },
    honeybadger: { key: 'hbp_MzPef38LdkNC9yPyJR5lXdOIj9JBK13PXI9e', environment: 'development' },
    google: {
        clientId: '151135267675-k0d1vsntkpf64t2n53argbn425f3lukg.apps.googleusercontent.com',
        key: 'AIzaSyAJ9XglxoFZic-BS5ZzbkSAZHCbLPFzg-s',
        shareKey: 'AIzaSyBMRfxSnjskCX61J4FugyJ2-brMmbmfJiA',
        project: '151135267675',
        scope: ['https://www.googleapis.com/auth/drive', 'https://www.googleapis.com/auth/youtube.readonly'],
    },
    supportEmail: 'support@richardson.com',
    supportUrl: 'https://support.richardson.com/hc/en-us/requests/new',
};

// Copied from config.js's `qa` block, with CODE emptied and the API on this origin.
const defaults = {
    environment: 'qa',
    apiGateway: {
        CODE: '',
        LESSONLY: 'Xs1AHfExAfcnDpuwTkUZQHwlMA0kVQqHcRJA1T2AYvruAzFuKnKbMQ==',
        URL: origin + '/api/',
        JOB_URL: origin + '/api/',
    },
    azure: {
        clientId: 'd2fd24b7-0e80-4eb4-b851-2e8eb200811b', //This is your client ID
        authority: 'https://login.microsoftonline.com/common', //This is your tenant info
    },
    // HubSpot sends the user back to this site's /oauth/hubspot, and the API exchanges the code with
    // the same redirect_uri (its PortalBaseUrl). The HubSpot app must list each site's URL.
    hubspot: {
        auth: 'https://app.hubspot.com/oauth/authorize?client_id=b21b5f1e-5bb8-4198-9a21-a59de1996517&redirect_uri=' + encodeURIComponent(origin + '/oauth/hubspot') + '&scope=crm.lists.read%20crm.objects.contacts.read%20crm.schemas.contacts.read%20crm.objects.companies.read%20crm.objects.deals.read%20crm.schemas.companies.read%20crm.schemas.deals.read%20settings.users.read',
    },
    posthog: { key: 'phc_sYJzaIlwLcNDq5mM3rB661UQfLdNeGox0QuHoSL2gcm', survey: '01928bcc-06b8-0000-a482-96356af0a1af' },
    switches: {
        languages: true,
    },
    url: origin + '/'
};

// Keys the runtime file may set. A value must be a non-blank string; a section is merged one
// level deep, so { apiGateway: { URL } } keeps CODE and LESSONLY.
const OVERRIDABLE_VALUES = ['environment', 'url'];
// `build` names the running build for the Alt+I debug panel (E4-2443); it has no default.
const OVERRIDABLE_SECTIONS = ['azure', 'apiGateway', 'storage', 'switches', 'posthog', 'honeybadger', 'build'];

const isPlainObject = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const applyRuntimeConfig = (base, runtime) => {
    if (!isPlainObject(runtime)) {
        return base;
    }

    const configured = { ...base };

    OVERRIDABLE_VALUES.forEach(key => {
        if (typeof runtime[key] === 'string' && runtime[key].trim()) {
            configured[key] = runtime[key];
        }
    });

    OVERRIDABLE_SECTIONS.forEach(key => {
        if (isPlainObject(runtime[key])) {
            configured[key] = { ...base[key], ...runtime[key] };
        }
    });

    return configured;
};

export default applyRuntimeConfig({ ...shared, ...defaults }, window.__INSIGHTS_CONFIG__);
